import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { WaitingRoomWrapper, WaitingCard, WaitingHeader, WaitingMeta, Countdown, JoinButton, SlotsTable, TableWrap } from "./WaitingRoom.styles";
import { API_ENDPOINT } from "../../config/config";
import axiosInstance from "../../service/axiosInstance";
import Spinner from "../../components/Spinner/Spinner";
import { HOME } from "../../router/route-path";
import { useSelector } from "react-redux";
import { selectUser } from "../../app/features/user/userSlice";

const getUtcTimestamp = (value) => {
  if (!value) return null;
  const hasTimezone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(value);
  const normalized = hasTimezone ? value : `${value}Z`;
  const timestamp = Date.parse(normalized);
  return Number.isNaN(timestamp) ? null : timestamp;
};

const WaitingRoom = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userInfo } = useSelector(selectUser);
  const [eventConfig, setEventConfig] = useState(null);
  const [slots, setSlots] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loadingEvent, setLoadingEvent] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [updatingSlot, setUpdatingSlot] = useState(null);
  const startDraftRequestedRef = useRef(false);

  useEffect(() => {
    if (!id) return;
    let isMounted = true;
    const fetchEvent = async () => {
      setLoadingEvent(true);
      try {
        const response = await axiosInstance.get(
          `${API_ENDPOINT}event-config/${id}/`
        );
        if (isMounted) {
          setEventConfig(response.data);
        }
      } catch (error) {
        if (isMounted) {
          setEventConfig(null);
        }
      } finally {
        if (isMounted) {
          setLoadingEvent(false);
        }
      }
    };
    fetchEvent();
    return () => {
      isMounted = false;
    };
  }, [id]);

  useEffect(() => {
    startDraftRequestedRef.current = false;
  }, [id]);

  const startDraftOnce = useCallback(async () => {
    if (!id || startDraftRequestedRef.current) return;
    startDraftRequestedRef.current = true;
    console.log("[WaitingRoom] countdown reached 0 -> starting draft");
    try {
      const response = await axiosInstance.post(
        `${API_ENDPOINT}event-config/${id}/start/`
      );
      if (response?.data) {
        setEventConfig((prev) => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error("[WaitingRoom] failed to start draft", error);
    }
  }, [id]);

  useEffect(() => {
    if (!eventConfig?.status) return;
    if (eventConfig.status !== "waiting") {
      console.log("[WaitingRoom] draft already started, syncing state");
    }
  }, [eventConfig?.status]);

  const fetchSlots = useCallback(async () => {
    if (!id) return;
    //setLoadingSlots(true);
    try {
      const response = await axiosInstance.get(
        `${API_ENDPOINT}event-config/${id}/slots/`
      );
      setSlots(response.data || []);
    } catch (error) {
      setSlots([]);
    } finally {
      //setLoadingSlots(false);
    }
  }, [id]);

  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchSlots();
    }, 7000);
    return () => clearInterval(interval);
  }, [fetchSlots]);

  useEffect(() => {
    let isMounted = true;
    const fetchTeams = async () => {
      setLoadingTeams(true);
      try {
        const response = await axiosInstance.get(
          `${API_ENDPOINT}trade-value-history/?limit=100&offset=0&round=&round_index_number=7&tm=`
        );
        const results = response?.data?.results || [];
        const teamSet = [];
        results.forEach((team) => {
          const teamRound = team.round;
          const exists = teamSet.some((entry) => entry.name === teamRound.name);
          if (!exists) {
            teamSet.push({
              ...teamRound,
              selection: team.index,
              draftCountId: team.team_neads_info.id,
            });
          }
        });
        const orderedTeams = teamSet.sort(
          (a, b) => (a.selection ?? 0) - (b.selection ?? 0)
        );
        if (isMounted) {
          setTeams(orderedTeams);
        }
      } catch (error) {
        if (isMounted) {
          setTeams([]);
        }
      } finally {
        if (isMounted) {
          setLoadingTeams(false);
        }
      }
    };
    fetchTeams();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!eventConfig?.selected_date_time) {
      setTimeLeft(0);
      return;
    }
    if (eventConfig?.status && eventConfig.status !== "waiting") {
      setTimeLeft(0);
      return;
    }
    const targetTime = getUtcTimestamp(eventConfig.selected_date_time);
    if (!targetTime) {
      setTimeLeft(0);
      return;
    }
    const updateTime = () => {
      const now = Date.now();
      const diffMs = targetTime - now;
      const diffInSeconds = Math.max(Math.floor(diffMs / 1000), 0);
      setTimeLeft(diffInSeconds);
      if (diffMs <= 0 && eventConfig?.status === "waiting") {
        startDraftOnce();
      }
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [eventConfig?.selected_date_time, eventConfig?.status, startDraftOnce]);

  const formattedCountdown = useMemo(() => {
    const hours = Math.floor(timeLeft / 3600);
    const minutes = Math.floor((timeLeft % 3600) / 60);
    const seconds = timeLeft % 60;
    return [hours, minutes, seconds]
      .map((value) => String(value).padStart(2, "0"))
      .join(" : ");
  }, [timeLeft]);

  const startTimeLabel = useMemo(() => {
    if (!eventConfig?.selected_date_time) return "TBD";
    const targetTime = getUtcTimestamp(eventConfig.selected_date_time);
    if (!targetTime) return "TBD";
    return new Date(targetTime).toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }, [eventConfig?.selected_date_time]);

  const slotMap = useMemo(() => {
    const map = new Map();
    slots.forEach((slot) => {
      if (slot?.slot != null) {
        map.set(slot.slot, slot);
      }
    });
    return map;
  }, [slots]);

  const slotRows = useMemo(() => {
    if (slots.length) {
      return slots.map((slot, index) => {
        const team =
          teams.find((t) => t.id === slot.team_id)
          || teams.find((t) => t.name === slot.team_name)
          || null;
        const userId = slot?.user?.id ?? slot?.user_id ?? null;
        const userFullName = [slot?.user?.first_name, slot?.user?.last_name]
          .filter(Boolean)
          .join(" ")
          .trim();
        const userName = userFullName || slot?.user?.username || slot?.user_name || "";
        const isSelected = Boolean(
          userInfo?.id && userId && String(userInfo.id) === String(userId)
        );
        const slotLabel = userId
          ? (isSelected ? "You" : (userName || "Slot Filled"))
          : "Slot Available";
        return {
          slot: slot.slot ?? index + 1,
          teamName: slot.team_name || team?.name || "Team",
          teamLogo: team?.logo || "",
          slotLabel,
          isSelected,
          isAvailable: !userId,
        };
      });
    }
    if (!teams.length) {
      return [];
    }
    return teams
      .slice()
      .sort((a, b) => (a.selection ?? 0) - (b.selection ?? 0))
      .map((team, index) => ({
        slot: team.index ?? index + 1,
        teamName: team.name,
        teamLogo: team.logo || "",
        slotLabel: "Slot Available",
        isSelected: false,
        isAvailable: true,
      }));
  }, [slots, teams, userInfo]);

  const leagueSize = slotRows.length || teams.length || 0;
  const isDraftOpen = eventConfig?.status && eventConfig.status !== "waiting"
    ? true
    : timeLeft <= 0;
  const normalizedStatus = String(eventConfig?.status || "").toLowerCase();
  const isEventFinished = normalizedStatus === "completed" || normalizedStatus === "finished";
  const isUserAssigned = slotRows.some((row) => row.isSelected);
  const availableSlots = useMemo(
    () => slotRows.filter((row) => row.isAvailable),
    [slotRows]
  );
  const sortedSlotRows = useMemo(
    () => slotRows.slice().sort((a, b) => (a.slot ?? 0) - (b.slot ?? 0)),
    [slotRows]
  );

  const handleSlotSelect = async (slotNumber) => {
    if (!eventConfig?.id || !userInfo?.id || updatingSlot) return;
    setUpdatingSlot(slotNumber);
    try {
      await axiosInstance.patch(
        `${API_ENDPOINT}event-config/${eventConfig.id}/slots/${slotNumber}/`,
        { user_id: userInfo.id }
      );
      await fetchSlots();
    } finally {
      setUpdatingSlot(null);
    }
  };

  const handleJoinLeague = async () => {
    if (isUserAssigned) {
      if (isDraftOpen) {
        navigate(`/draft-room-v2/${id}`);
      }
      return;
    }
    if (!availableSlots.length || updatingSlot) return;
    const randomIndex = Math.floor(Math.random() * availableSlots.length);
    const selectedSlot = availableSlots[randomIndex];
    await handleSlotSelect(selectedSlot.slot);
  };

  const isLoading = loadingEvent || loadingSlots || loadingTeams;

  return (
    <WaitingRoomWrapper>
      <WaitingCard>
        {isLoading && (
          <div className="waiting-loading">
            <div className="waiting-title">Waiting Room</div>
          </div>
        )}
        {!isLoading && (
          <>
            {isEventFinished ? (
              <div className="waiting-finished">
                <h2>The event you&apos;re trying to access has already finished.</h2>
                <div className="divider" />
                <p className="home-copy">
                  Go to the{" "}
                  <button
                    type="button"
                    className="home-link"
                    onClick={() => navigate(HOME)}
                  >
                    Draft Professor Homepage.
                  </button>
                </p>
              </div>
            ) : (
              <>
                <WaitingMeta>
                  <div className="meta-col">
                    <div className="meta-row">
                      <span className="label">Rounds:</span>
                      <span className="value">{eventConfig?.rounds ?? "-"}</span>
                    </div>
                    <div className="meta-row">
                      <span className="label">Room Type:</span>
                      <span className="value">
                        {eventConfig?.room_type
                          ? String(eventConfig.room_type).toUpperCase()
                          : "-"}
                      </span>
                    </div>
                    <div className="meta-row">
                      <span className="label">Multi-day Draft:</span>
                      <span className="value">
                        {eventConfig?.multi_day ? "Yes" : "No"}
                      </span>
                    </div>
                  </div>
                  <div className="meta-col center">
                    {isDraftOpen ? (
                      <div className="draft-open">The draft room is now open!</div>
                    ) : (
                      <>
                        <div className="starts">This Draft Starts: {startTimeLabel}</div>
                        <Countdown>{formattedCountdown}</Countdown>
                      </>
                    )}
                  </div>
                  <div className="meta-col action">
                    <JoinButton
                      onClick={handleJoinLeague}
                      disabled={
                        isUserAssigned ? !isDraftOpen : !availableSlots.length || updatingSlot
                      }
                    >
                      {isUserAssigned ? "Enter The Draft" : "Join This League"}
                    </JoinButton>
                  </div>
                </WaitingMeta>
                <TableWrap>
                  <SlotsTable>
                    <thead>
                      <tr>
                        <th>ORDER</th>
                        <th>TEAM</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedSlotRows.map((row) => {
                        const canSelectRow = row.isAvailable && !updatingSlot;
                        return (
                          <tr
                            key={`slot-${row.slot}`}
                            className={`${row.isSelected ? "selected" : ""} ${canSelectRow ? "available" : ""} ${!row.isAvailable ? "occupied" : ""}`}
                            onClick={canSelectRow ? () => handleSlotSelect(row.slot) : undefined}
                          >
                            <td>{row.slot}</td>
                            <td className="team-cell">
                              {row.teamLogo ? (
                                <img
                                  className="team-logo"
                                  src={row.teamLogo}
                                  alt={`${row.teamName} logo`}
                                />
                              ) : null}
                              <span>{row.teamName}</span>
                            </td>
                            <td className="slot">
                              {updatingSlot === row.slot ? "Updating..." : row.slotLabel}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </SlotsTable>
                </TableWrap>
              </>
            )}
          </>
        )}
      </WaitingCard>
    </WaitingRoomWrapper>
  );
};

export default WaitingRoom;
